import { AppState } from '~src/bootstrap/redux/state/AppState';

/**
 * Selectors for site admin resources state.
 * 
 * These selectors provide access to which admin resource types
 * (User, Group, Task, Job) the current user can access.
 */

/**
 * Get the set of allowed site admin resources for the current user.
 * 
 * @returns Set of resource type names (e.g., 'User', 'Group', 'Task', 'Job')
 *          undefined if not yet fetched
 *          empty Set if fetched but user has no permissions
 */
export function getAllowedSiteAdminResources(state: AppState): Set<string> | undefined {
  return state.siteAdminResources.allowedResources;
}

/**
 * Check if we're currently fetching site admin resources.
 * 
 * @returns true if API call is in progress, false otherwise
 */
export function isFetchingSiteAdminResources(state: AppState): boolean {
  return state.siteAdminResources.isFetching;
}

/**
 * Check if a specific resource type is allowed for the current user.
 * 
 * @param state - Redux state
 * @param resourceType - Resource type name (e.g., 'User', 'Group', 'Task', 'Job')
 * @returns true if the resource is allowed, false otherwise
 *          Returns false if not yet fetched (undefined)
 */
export function isResourceAllowed(state: AppState, resourceType: string): boolean {
  const allowedResources = getAllowedSiteAdminResources(state);
  return allowedResources?.has(resourceType) ?? false;
}

/**
 * Check if any of the provided resource types are allowed.
 * 
 * @param state - Redux state
 * @param resourceTypes - Array of resource type names
 * @returns true if at least one resource type is allowed, false otherwise
 */
export function isAnyResourceAllowed(state: AppState, resourceTypes: readonly string[]): boolean {
  const allowedResources = getAllowedSiteAdminResources(state);
  if (!allowedResources) {
    return false;
  }
  return resourceTypes.some(type => allowedResources.has(type));
}
